// src/components/KegTracking.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Keg,
  KegTransaction,
  KegTrackingProps,
  Location,
  Product,
} from '../types/interfaces';
import KegScanOverlay from './KegScanOverlay';
import { primeScanAudio } from '../utils/scanFeedback';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { isValidKegCode, normalizeKegCode } from '../utils/kegCode';
import { isKegProductClearedStatus, manualUpdatePrefillCode } from '../utils/kegManualUpdate';
import {
  formatLastTransactionLabel,
  KEG_OLDER_THAN_OPTIONS,
} from '../utils/kegDates';

/** Mobile/desktop page size — keeps ~1.5k keg rows usable without virtualization. */
export const KEG_LIST_PAGE_SIZE = 50;

const KegTracking: React.FC<KegTrackingProps> = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const deepLinkHandled = useRef<string | null>(null);
  // Latest params for async callers (scan → lookup → open details).
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  /** Tag whose Keg Details we pushed a history entry for (?details=EK…), so Back closes it. */
  const detailsPushed = useRef<string | null>(null);
  const [kegs, setKegs] = useState<Keg[]>([]);
  const [newKegCode, setNewKegCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [detailsModal, setDetailsModal] = useState<{ keg: Keg; transactions: KegTransaction[] } | null>(
    null
  );
  const [updateModal, setUpdateModal] = useState<Keg | null>(null);
  const [updateForm, setUpdateForm] = useState({
    status: '',
    locationId: '',
    lastScanned: '',
    productId: '',
  });
  const [assetLocations, setAssetLocations] = useState<Location[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  /** Catalog names like "Keg - 1/6 bbl" — UI filter values; API matches shell sizes by size key. */
  const [kegPackageCatalog, setKegPackageCatalog] = useState<string[]>([]);
  const [filterLocationId, setFilterLocationId] = useState('');
  const [filterPackagingType, setFilterPackagingType] = useState('');
  const [filterProductId, setFilterProductId] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterOlderThanDays, setFilterOlderThanDays] = useState('');
  const [sortBy, setSortBy] = useState<'code' | 'lastTransaction'>('code');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [listPage, setListPage] = useState(0);
  /** Tracks ≤767px so jsdom viewport tests can assert card visibility (CSS media queries alone are unreliable in Jest). */
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 767
  );

  useEffect(() => {
    const apply = () => setIsNarrow(window.innerWidth <= 767);
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  const emptyUpdateForm = () => ({
    status: '',
    locationId: '',
    lastScanned: '',
    productId: '',
  });

  const isOutdoorRacksEmpty = (locationId: string) => {
    const loc = assetLocations.find((l) => String(l.locationId) === String(locationId));
    return /outdoor racks \(empty\)/i.test(String(loc?.name || ''));
  };

  const isAssetDestroyedLocation = (locationId: string) => {
    const loc = assetLocations.find((l) => String(l.locationId) === String(locationId));
    return /^asset destroyed$/i.test(String(loc?.name || '').trim());
  };

  /** Empty, Destroyed or Broken kegs (or kegs on the racks / Asset Destroyed) hold no product. */
  const productLocked = (status: string, locationId: string) =>
    isKegProductClearedStatus(status) ||
    isOutdoorRacksEmpty(locationId) ||
    isAssetDestroyedLocation(locationId);

  const loadKegs = async () => {
    const params = new URLSearchParams();
    if (filterLocationId) params.set('locationId', filterLocationId);
    if (filterPackagingType) params.set('packagingType', filterPackagingType);
    if (filterProductId) params.set('productId', filterProductId);
    if (filterTag.trim()) params.set('q', filterTag.trim());
    if (filterOlderThanDays) params.set('olderThanDays', filterOlderThanDays);
    if (sortBy === 'lastTransaction') {
      params.set('sort', 'lastTransaction');
      params.set('order', sortOrder);
    }
    const qs = params.toString();
    const res = await apiFetch(`${API_BASE_URL}/api/kegs${qs ? `?${qs}` : ''}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Failed to fetch kegs: HTTP ${res.status}`);
    const data = await res.json();
    setKegs(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    const boot = async () => {
      try {
        const [locRes, prodRes, pkgRes] = await Promise.all([
          apiFetch(`${API_BASE_URL}/api/kegs/asset-locations`, {
            headers: { Accept: 'application/json' },
          }),
          apiFetch(`${API_BASE_URL}/api/products`, { headers: { Accept: 'application/json' } }),
          apiFetch(`${API_BASE_URL}/api/package-types`, { headers: { Accept: 'application/json' } }),
        ]);
        if (!locRes.ok) throw new Error(`Failed to fetch keg locations: HTTP ${locRes.status}`);
        if (!prodRes.ok) throw new Error(`Failed to fetch products: HTTP ${prodRes.status}`);
        setAssetLocations(await locRes.json());
        setProducts(await prodRes.json());
        if (pkgRes.ok) {
          const pkgs = await pkgRes.json();
          const kegNames = (Array.isArray(pkgs) ? pkgs : [])
            .map((p: { name?: string }) => String(p.name || '').trim())
            .filter((name: string) => /^keg\s*-/i.test(name));
          setKegPackageCatalog(kegNames.sort((a: string, b: string) => a.localeCompare(b)));
        }
      } catch (err: any) {
        setError('Failed to load keg form data: ' + err.message);
      }
    };
    boot();
  }, []);

  useEffect(() => {
    loadKegs().catch((err: any) => setError('Failed to load kegs: ' + err.message));
  }, [
    filterLocationId,
    filterPackagingType,
    filterProductId,
    filterTag,
    filterOlderThanDays,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    setListPage(0);
  }, [
    filterLocationId,
    filterPackagingType,
    filterProductId,
    filterTag,
    filterOlderThanDays,
    sortBy,
    sortOrder,
    kegs.length,
  ]);

  const toggleLastTransactionSort = () => {
    if (sortBy !== 'lastTransaction') {
      setSortBy('lastTransaction');
      setSortOrder('asc'); // oldest first — likely lost
      return;
    }
    setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
  };

  // Prefer catalog "Keg - 1/6 bbl" labels (what product packages use); fall back to shell names on kegs.
  const packagingTypeOptions = useMemo(() => {
    if (kegPackageCatalog.length) return kegPackageCatalog;
    const set = new Set<string>();
    for (const k of kegs) {
      if (k.packagingType) set.add(k.packagingType);
    }
    return Array.from(set).sort();
  }, [kegPackageCatalog, kegs]);

  const totalPages = Math.max(1, Math.ceil(kegs.length / KEG_LIST_PAGE_SIZE));
  const safePage = Math.min(listPage, totalPages - 1);
  const pageKegs = useMemo(() => {
    const start = safePage * KEG_LIST_PAGE_SIZE;
    return kegs.slice(start, start + KEG_LIST_PAGE_SIZE);
  }, [kegs, safePage]);

  const openKegDetails = async (keg: Keg, opts: { history?: boolean } = { history: true }) => {
    const res = await apiFetch(
      `${API_BASE_URL}/api/kegs/${encodeURIComponent(keg.code)}/transactions`,
      { headers: { Accept: 'application/json' } }
    );
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to fetch transactions: ${text}`);
    }
    const transactions = await res.json();
    setDetailsModal({ keg, transactions: Array.isArray(transactions) ? transactions : [] });
    if (!opts.history) return;
    // Phone/browser Back closes Keg Details and stays on Keg Tracking:
    // opening adds one history entry (?details=EK09503); Back pops it.
    const code = normalizeKegCode(keg.code);
    const current = searchParamsRef.current;
    if (current.get('details') === code) {
      detailsPushed.current = detailsPushed.current || code;
      return;
    }
    const next = new URLSearchParams(current);
    next.delete('keg');
    next.delete('code');
    next.set('details', code);
    const alreadyOpen = Boolean(current.get('details'));
    detailsPushed.current = code;
    setSearchParams(next, { replace: alreadyOpen });
  };

  /** Close Keg Details. If we added the history entry, go back over it (same as the phone's Back). */
  const closeKegDetails = () => {
    setDetailsModal(null);
    if (detailsPushed.current && searchParamsRef.current.get('details')) {
      detailsPushed.current = null;
      navigate(-1);
      return;
    }
    detailsPushed.current = null;
    if (searchParamsRef.current.get('details')) {
      const next = new URLSearchParams(searchParamsRef.current);
      next.delete('details');
      setSearchParams(next, { replace: true });
    }
  };

  // Back (or Forward / reload) changes ?details=: keep the modal in step with the URL.
  const detailsParam = searchParams.get('details');
  useEffect(() => {
    if (!detailsParam) {
      if (detailsPushed.current) {
        detailsPushed.current = null;
        setDetailsModal(null);
      }
      return;
    }
    const code = normalizeKegCode(detailsParam);
    if (detailsModal && normalizeKegCode(detailsModal.keg.code) === code) return;
    // Forward into an entry / reload with ?details=: reopen without adding history.
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/kegs/${encodeURIComponent(code)}`, {
          headers: { Accept: 'application/json' },
          allowHttpError: true,
        });
        if (!res.ok || cancelled) return;
        const keg = await res.json();
        if (cancelled) return;
        detailsPushed.current = code;
        await openKegDetails(keg, { history: false });
      } catch {
        // leave the page as is
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailsParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link from inventory history chips: /keg-tracking?keg=EK09503
  useEffect(() => {
    const raw = searchParams.get('keg') || searchParams.get('code');
    if (!raw) return;
    const code = normalizeKegCode(raw);
    if (!code || deepLinkHandled.current === code) return;
    deepLinkHandled.current = code;

    const open = async () => {
      try {
        setFilterTag(code);
        const existing = kegs.find((k) => normalizeKegCode(k.code) === code);
        if (existing) {
          await openKegDetails(existing, { history: false });
        } else {
          const res = await apiFetch(`${API_BASE_URL}/api/kegs/${encodeURIComponent(code)}`, {
            headers: { Accept: 'application/json' },
            allowHttpError: true,
          });
          if (!res.ok) {
            setError(`Keg ${code} not found`);
            return;
          }
          const keg = await res.json();
          await openKegDetails(keg, { history: false });
        }
        const next = new URLSearchParams(searchParams);
        next.delete('keg');
        next.delete('code');
        setSearchParams(next, { replace: true });
      } catch (err: any) {
        setError('Failed to open keg: ' + (err.message || 'unknown error'));
      }
    };
    void open();
  }, [searchParams, kegs, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const openRegistration = (keg: Keg) => {
    setUpdateModal(keg);
    setUpdateForm({
      status: keg.status || 'Empty',
      locationId: keg.locationId != null ? String(keg.locationId) : '',
      lastScanned: keg.lastScanned || '',
      productId: keg.productId != null ? String(keg.productId) : '',
    });
  };

  /** Look up (or register) a keg. Returns an error message, or null when it worked. */
  const registerKeg = async (rawCode: string): Promise<string | null> => {
    const code = normalizeKegCode(rawCode);
    if (!isValidKegCode(code)) {
      const msg = 'Valid keg tag needed (e.g., EK09503)';
      setError(msg);
      return msg;
    }
    try {
      // Prefer lookup of an existing imported tag; register only when missing.
      const existingRes = await apiFetch(`${API_BASE_URL}/api/kegs/${encodeURIComponent(code)}`, {
        headers: { Accept: 'application/json' },
        allowHttpError: true,
      });
      if (existingRes.ok) {
        const existing = await existingRes.json();
        setKegs((prev) => {
          if (prev.some((k) => normalizeKegCode(k.code) === code)) return prev;
          return [...prev, existing].sort((a, b) => a.code.localeCompare(b.code));
        });
        setSuccessMessage(`Found keg ${code}`);
        setTimeout(() => setSuccessMessage(null), 2000);
        setNewKegCode('');
        setShowScanner(false);
        setError(null);
        await openKegDetails(existing);
        await loadKegs();
        return null;
      }
      const res = await apiFetch(`${API_BASE_URL}/api/kegs/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to register keg: ${text}`);
      }
      const newKeg = await res.json();
      setKegs((prev) => [...prev, newKeg].sort((a, b) => a.code.localeCompare(b.code)));
      setSuccessMessage('Keg registered — complete details');
      setTimeout(() => setSuccessMessage(null), 2000);
      setNewKegCode('');
      setShowScanner(false);
      setError(null);
      openRegistration(newKeg);
      await loadKegs();
      return null;
    } catch (err: any) {
      const msg = 'Failed to register keg: ' + err.message;
      setError(msg);
      return msg;
    }
  };

  const handleViewDetails = async (keg: Keg) => {
    try {
      await openKegDetails(keg);
    } catch (err: any) {
      setError('Failed to load keg transactions: ' + err.message);
    }
  };

  /**
   * Manual Update button: carry over a keg tag already typed in the
   * "Enter keg tag" box or the "Search tag" filter, and load that keg's current
   * status/location/product so nothing has to be typed twice.
   */
  const openManualUpdate = async () => {
    // A partial search (e.g. "EK084") that narrows the list to one keg uses that keg.
    const search = normalizeKegCode(filterTag);
    const onlyMatch =
      search && kegs.length === 1 && normalizeKegCode(kegs[0].code).includes(search)
        ? kegs[0].code
        : '';
    const code = manualUpdatePrefillCode(newKegCode, onlyMatch, filterTag);
    if (!code) {
      setUpdateModal({ id: 0, code: '', status: '', lastScanned: '' });
      setUpdateForm(emptyUpdateForm());
      return;
    }
    const known = kegs.find((k) => normalizeKegCode(k.code) === code);
    if (known) {
      openRegistration(known);
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/kegs/${encodeURIComponent(code)}`, {
        headers: { Accept: 'application/json' },
        allowHttpError: true,
      });
      if (res.ok) {
        openRegistration(await res.json());
        return;
      }
    } catch {
      /* fall through: still prefill the tag */
    }
    setUpdateModal({ id: 0, code, status: '', lastScanned: '' });
    setUpdateForm(emptyUpdateForm());
  };

  const handleManualUpdate = async () => {
    if (!updateModal) return;
    const code = normalizeKegCode(updateModal.code);
    if (!isValidKegCode(code)) {
      setError('Valid keg tag needed (e.g., EK09503)');
      return;
    }
    let { status, locationId, lastScanned, productId } = updateForm;
    if (productLocked(status, locationId)) {
      productId = '';
    }
    if (!status && !locationId && !lastScanned && !productId) {
      setError('At least one field (status, location, date, or product) must be provided');
      return;
    }
    try {
      const updateData: {
        status: string;
        locationId?: string;
        lastScanned?: string;
        productId?: number | null;
      } = {
        status: status || updateModal.status,
      };
      if (locationId) updateData.locationId = locationId;
      if (lastScanned) updateData.lastScanned = lastScanned;
      if (productId) updateData.productId = parseInt(productId, 10);
      else if (productLocked(status, locationId)) {
        updateData.productId = null;
      }
      const res = await apiFetch(`${API_BASE_URL}/api/kegs/${encodeURIComponent(code)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(updateData),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to update keg: ${text}`);
      }
      const updated = await res.json();
      setSuccessMessage('Keg updated successfully');
      setTimeout(() => setSuccessMessage(null), 2000);
      setUpdateModal(null);
      setUpdateForm(emptyUpdateForm());
      setError(null);
      await loadKegs();
      if (updated?.code) {
        await openKegDetails({ ...updateModal, ...updated });
      }
    } catch (err: any) {
      setError('Failed to update keg: ' + err.message);
    }
  };

  const renderPagination = () => {
    if (kegs.length <= KEG_LIST_PAGE_SIZE) return null;
    const from = safePage * KEG_LIST_PAGE_SIZE + 1;
    const to = Math.min(kegs.length, (safePage + 1) * KEG_LIST_PAGE_SIZE);
    return (
      <div className="keg-list-pagination" data-testid="keg-list-pagination">
        <button
          type="button"
          data-testid="keg-list-prev"
          disabled={safePage <= 0}
          onClick={() => setListPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <span data-testid="keg-list-page-label">
          {from}–{to} of {kegs.length} (page {safePage + 1}/{totalPages})
        </span>
        <button
          type="button"
          data-testid="keg-list-next"
          disabled={safePage >= totalPages - 1}
          onClick={() => setListPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
      </div>
    );
  };

  const productDisabled = productLocked(updateForm.status, updateForm.locationId);

  return (
    <div
      className="page-container"
      data-testid="keg-tracking-page"
      data-narrow={isNarrow ? 'true' : 'false'}
    >
      <h2 className="app-header" style={{ color: '#EEC930', fontSize: '24px', marginBottom: '20px' }}>
        Keg Tracking
      </h2>
      {error && <div className="error">{error}</div>}
      {successMessage && (
        <div className="error" style={{ color: '#28A745', backgroundColor: '#e6ffe6' }}>
          {successMessage}
        </div>
      )}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={() => {
            primeScanAudio(); // iOS: sound needs this tap
            setShowScanner(true);
          }}
          style={{
            backgroundColor: '#2196F3',
            color: '#fff',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Scan Keg
        </button>
        <input
          type="text"
          value={newKegCode}
          onChange={(e) => setNewKegCode(e.target.value)}
          placeholder="Enter keg tag (e.g., EK09503)"
          style={{ padding: '10px', width: '200px' }}
          disabled={showScanner}
        />
        <button
          onClick={() => registerKeg(newKegCode)}
          style={{
            backgroundColor: '#2196F3',
            color: '#fff',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
          disabled={showScanner}
        >
          Find / Register
        </button>
        <button
          data-testid="keg-manual-update-button"
          onClick={() => void openManualUpdate()}
          style={{
            backgroundColor: '#28A745',
            color: '#fff',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Manual Update
        </button>
        <a
          href="/keg-returns"
          data-testid="keg-return-button"
          style={{
            backgroundColor: '#F86752',
            color: '#fff',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'inline-block',
          }}
        >
          Return
        </a>
        <a
          href="/keg-returns?type=taproom"
          data-testid="keg-taproom-return-button"
          style={{
            backgroundColor: '#8E5A2B',
            color: '#fff',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'inline-block',
          }}
        >
          Taproom Return
        </a>
        <a
          href="/taproom-keg-counts"
          data-testid="keg-taproom-count-button"
          style={{
            backgroundColor: '#5B3A1C',
            color: '#fff',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'inline-block',
          }}
        >
          Taproom Keg Count
        </a>
      </div>

      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <input
          type="text"
          value={filterTag}
          onChange={(e) => setFilterTag(e.target.value)}
          placeholder="Search tag"
          style={{ padding: '8px', width: '140px' }}
        />
        <select
          value={filterLocationId}
          onChange={(e) => setFilterLocationId(e.target.value)}
          style={{ padding: '8px' }}
        >
          <option value="">All locations</option>
          {assetLocations.map((loc) => (
            <option key={loc.locationId} value={loc.locationId}>
              {loc.name}
            </option>
          ))}
        </select>
        <select
          value={filterPackagingType}
          onChange={(e) => setFilterPackagingType(e.target.value)}
          style={{ padding: '8px' }}
        >
          <option value="">All keg types</option>
          {packagingTypeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={filterProductId}
          onChange={(e) => setFilterProductId(e.target.value)}
          style={{ padding: '8px' }}
        >
          <option value="">All products</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={filterOlderThanDays}
          onChange={(e) => setFilterOlderThanDays(e.target.value)}
          style={{ padding: '8px' }}
          data-testid="keg-older-than-filter"
          aria-label="Last transaction older than"
        >
          <option value="">Any last transaction</option>
          {KEG_OLDER_THAN_OPTIONS.map((d) => (
            <option key={d} value={String(d)}>
              Older than {d} days
            </option>
          ))}
        </select>
        <button
          type="button"
          className="keg-sort-header"
          data-testid="keg-sort-last-transaction-btn"
          onClick={toggleLastTransactionSort}
          style={{ padding: '8px 12px' }}
        >
          Sort: Last Transaction
          {sortBy === 'lastTransaction' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''}
        </button>
        <span style={{ color: '#555', fontWeight: 600 }} data-testid="keg-showing-count">
          Showing {kegs.length} keg{kegs.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="inventory-table-container">
        <table className="inventory-table" data-testid="keg-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Product</th>
              <th>Keg Type</th>
              <th>Status</th>
              <th>Location</th>
              <th>
                <button
                  type="button"
                  className="keg-sort-header"
                  data-testid="keg-sort-last-transaction"
                  onClick={toggleLastTransactionSort}
                  title="Sort by last transaction"
                >
                  Last Transaction
                  {sortBy === 'lastTransaction' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageKegs.map((keg) => (
              <tr key={keg.code}>
                <td>
                  <span
                    onClick={() => handleViewDetails(keg)}
                    style={{ color: '#2196F3', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {keg.code}
                  </span>
                </td>
                <td>{keg.productName || 'N/A'}</td>
                <td>{keg.packagingType || 'N/A'}</td>
                <td>{keg.status}</td>
                <td>{keg.locationName || 'N/A'}</td>
                <td data-testid={`keg-last-tx-${keg.code}`}>
                  {formatLastTransactionLabel(keg.lastTransactionDate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="keg-card-list" data-testid="keg-card-list">
          {pageKegs.length === 0 ? (
            <div className="alert-info">No kegs found.</div>
          ) : (
            pageKegs.map((keg) => (
              <div
                key={keg.code}
                className="keg-card-item"
                data-testid={`keg-card-${keg.code}`}
                onClick={() => handleViewDetails(keg)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    void handleViewDetails(keg);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <p className="keg-card-code">{keg.code}</p>
                <p>
                  <strong>Product:</strong> {keg.productName || 'N/A'}
                </p>
                <p>
                  <strong>Keg Type:</strong> {keg.packagingType || 'N/A'}
                </p>
                <p>
                  <strong>Status:</strong> {keg.status}
                </p>
                <p>
                  <strong>Location:</strong> {keg.locationName || 'N/A'}
                </p>
                <p data-testid={`keg-last-tx-${keg.code}`}>
                  <strong>Last Transaction:</strong>{' '}
                  {formatLastTransactionLabel(keg.lastTransactionDate)}
                </p>
              </div>
            ))
          )}
        </div>
        {renderPagination()}
      </div>

      {showScanner && (
        <KegScanOverlay
          title="Scan Keg"
          subtitle="Look up a keg by its tag"
          codes={[]}
          onScan={async (code) => {
            const normalized = normalizeKegCode(code);
            setNewKegCode(normalized);
            const err = await registerKeg(normalized);
            if (err) throw new Error(err);
            return { message: `Found ${normalized}` };
          }}
          doneLabel="Close"
          onDone={() => setShowScanner(false)}
        />
      )}
      {detailsModal && (
        <div
          data-testid="keg-details-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2100,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              width: 'min(600px, 92vw)',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              textAlign: 'center',
              color: '#555',
            }}
          >
            {/* Sticky header: keg tag + Close stay visible while the (long) history scrolls. */}
            <div
              data-testid="keg-details-header"
              style={{
                position: 'sticky',
                top: '-20px',
                margin: '-20px -20px 16px',
                padding: '12px 16px',
                backgroundColor: '#fff',
                borderBottom: '1px solid #ddd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                zIndex: 1,
                borderRadius: '8px 8px 0 0',
              }}
            >
              <h3 style={{ color: '#555', margin: 0, fontSize: '1.15rem', textAlign: 'left' }}>
                Keg Details: <span data-testid="keg-details-code">{detailsModal.keg.code}</span>
              </h3>
              <button
                type="button"
                data-testid="keg-details-close"
                onClick={closeKegDetails}
                style={{
                  backgroundColor: '#F86752',
                  color: '#fff',
                  padding: '10px 18px',
                  minHeight: '44px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  flex: '0 0 auto',
                }}
              >
                Close
              </button>
            </div>
            <p>
              <strong>Status:</strong> {detailsModal.keg.status}
            </p>
            <p>
              <strong>Product:</strong> {detailsModal.keg.productName || 'N/A'}
            </p>
            <p>
              <strong>Keg Type:</strong> {detailsModal.keg.packagingType || 'N/A'}
            </p>
            <p>
              <strong>Location:</strong> {detailsModal.keg.locationName || 'N/A'}
            </p>
            <p>
              <strong>Last Scanned:</strong> {detailsModal.keg.lastScanned}
            </p>
            <h4
              style={{ color: '#555', margin: '20px 0 6px', fontSize: '0.95rem' }}
              data-testid="keg-history-heading"
            >
              Keg history
            </h4>
            <p
              className="text-muted"
              style={{ fontSize: '0.8rem', margin: '0 0 10px', color: '#777' }}
            >
              Status changes and imports (newest first). Imported Ekos rows never change current keg status.
            </p>
            <table
              data-testid="keg-history-table"
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}
            >
              <thead>
                <tr style={{ backgroundColor: '#f4f4f4' }}>
                  <th style={{ border: '1px solid #ddd', padding: '6px 8px' }}>Date</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px 8px' }}>Status</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px 8px' }}>Reason</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px 8px' }}>Location</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px 8px' }}>Batch</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px 8px' }}>Customer</th>
                </tr>
              </thead>
              <tbody>
                {detailsModal.transactions.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ border: '1px solid #ddd', padding: '8px', color: '#888' }}
                    >
                      No history yet.
                    </td>
                  </tr>
                )}
                {detailsModal.transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td style={{ border: '1px solid #ddd', padding: '6px 8px' }}>{tx.date}</td>
                    <td style={{ border: '1px solid #ddd', padding: '6px 8px' }}>{tx.action}</td>
                    <td style={{ border: '1px solid #ddd', padding: '6px 8px' }}>
                      {tx.reason || '—'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '6px 8px' }}>
                      {tx.location || 'N/A'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '6px 8px' }}>
                      {tx.batchId || tx.batchCode || '—'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '6px 8px' }}>
                      {tx.customerName || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {updateModal && (
        <div
          data-testid="keg-update-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2100,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              width: 'min(400px, 92vw)',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              textAlign: 'center',
              color: '#555',
            }}
          >
            <h3 style={{ color: '#555', marginBottom: '20px' }}>Manual Update Keg</h3>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Keg Tag:</label>
              <input
                type="text"
                value={updateModal.code}
                data-testid="keg-update-code"
                onChange={(e) => setUpdateModal({ ...updateModal, code: e.target.value })}
                placeholder="Enter keg tag (e.g., EK09503)"
                style={{ padding: '10px', width: '100%', border: '1px solid #CCCCCC', borderRadius: '4px' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Status:</label>
              <select
                value={updateForm.status}
                data-testid="keg-update-status"
                onChange={(e) => {
                  const status = e.target.value;
                  setUpdateForm((prev) => {
                    // Destroyed: default the location to Asset Destroyed (can still be changed).
                    const destroyedLoc =
                      status === 'Destroyed'
                        ? assetLocations.find((l) => /^asset destroyed$/i.test(String(l.name || '').trim()))
                        : undefined;
                    const locationId = destroyedLoc ? String(destroyedLoc.locationId) : prev.locationId;
                    return {
                      ...prev,
                      status,
                      locationId,
                      productId: productLocked(status, locationId) ? '' : prev.productId,
                    };
                  });
                }}
                style={{ padding: '10px', width: '100%', border: '1px solid #CCCCCC', borderRadius: '4px' }}
              >
                <option value="">Select Status</option>
                <option value="Filled">Filled</option>
                <option value="Empty">Empty</option>
                <option value="At Customer">At Customer</option>
                <option value="Destroyed">Destroyed</option>
                <option value="Broken">Broken</option>
                <option value="Lost">Lost</option>
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Location:</label>
              <select
                value={updateForm.locationId}
                onChange={(e) => {
                  const locationId = e.target.value;
                  setUpdateForm((prev) => ({
                    ...prev,
                    locationId,
                    productId: productLocked(prev.status, locationId) ? '' : prev.productId,
                  }));
                }}
                style={{ padding: '10px', width: '100%', border: '1px solid #CCCCCC', borderRadius: '4px' }}
              >
                <option value="">Select Location</option>
                {assetLocations.map((loc) => (
                  <option key={loc.locationId} value={loc.locationId}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Last Scanned Date:</label>
              <input
                type="date"
                value={updateForm.lastScanned}
                onChange={(e) => setUpdateForm({ ...updateForm, lastScanned: e.target.value })}
                style={{ padding: '10px', width: '100%', border: '1px solid #CCCCCC', borderRadius: '4px' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Product:</label>
              <select
                value={updateForm.productId}
                data-testid="keg-update-product"
                onChange={(e) => setUpdateForm({ ...updateForm, productId: e.target.value })}
                disabled={productDisabled}
                style={{ padding: '10px', width: '100%', border: '1px solid #CCCCCC', borderRadius: '4px' }}
              >
                <option value="">
                  {productDisabled ? 'Cleared (Empty / Destroyed / Outdoor Racks)' : 'Select Product'}
                </option>
                {products.map((prod) => (
                  <option key={prod.id} value={prod.id}>
                    {prod.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Packaging Type:</label>
              <input
                type="text"
                data-testid="keg-packaging-readonly"
                value={updateModal.packagingType || 'N/A'}
                readOnly
                disabled
                style={{
                  padding: '10px',
                  width: '100%',
                  border: '1px solid #CCCCCC',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5',
                  color: '#555',
                }}
              />
              <small style={{ display: 'block', marginTop: '4px', color: '#777' }}>
                Package type is set at import/registration and cannot be changed here.
              </small>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '20px' }}>
              <button
                onClick={handleManualUpdate}
                style={{
                  backgroundColor: '#28A745',
                  color: '#fff',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Update
              </button>
              <button
                onClick={() => {
                  setUpdateModal(null);
                  setUpdateForm(emptyUpdateForm());
                }}
                style={{
                  backgroundColor: '#F86752',
                  color: '#fff',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KegTracking;
